import { useTranslation } from 'react-i18next';
import { useBilling } from '@/features/billing/hooks/useBilling';
import type { PersonalPlanState } from '@/features/billing/hooks/usePersonalPlanState';
import {
  formatElapsedPlanPeriodDate,
  formatPlanPeriodDate,
} from '@/features/billing/lib/formatPlanPeriodDate';
import {
  FAMILY_ANNUAL,
  PLUS_ANNUAL,
} from '@/features/billing/lib/getRedirectUrl';
import { SubscriptionPlan } from '@/types/gql.generated';
import { Box, Button, Text } from '@/ui';
import { InheritedAccessNote } from '../../../components/InheritedAccessNote';
import { PlanBadge } from '../../../components/PlanBadge';
import { PlanCard } from '../../../components/PlanCard';
import { useSettingsModal } from '../../../store';
import { ManageSubscriptionButton } from './ManageSubscriptionButton';
import { ManualPlanSupportNotice } from './ManualPlanSupportNotice';

type Props = {
  state: PersonalPlanState;
  endDate?: string | null;
};

// Reactivating should re-offer what they had, not silently downgrade a lapsed
// Family owner to Plus. A BUSINESS plan can't fund a personal org at all
// (PLAN_COVERED_ORG_TYPES on the server), so it can't reach this card — Plus is
// the safe fallback if it somehow does.
const REACTIVATE_PACKAGE = {
  [SubscriptionPlan.Plus]: PLUS_ANNUAL,
  [SubscriptionPlan.Family]: FAMILY_ANNUAL,
  [SubscriptionPlan.Business]: PLUS_ANNUAL,
} as const;

// The card at the top of the personal account panel. The badge is the signal of
// *where* the access comes from: "Current plan" for something bought on this
// account, "Personal plan" for access a workspace grants.
export const PersonalPlanCard = ({ state, endDate }: Props) => {
  const { t } = useTranslation('settingsModal', { keyPrefix: 'subscription' });
  const { changeTab } = useSettingsModal();
  const { redirectToCheckout } = useBilling();

  const formattedDate = formatPlanPeriodDate(endDate);

  // The title is the personal org's entitlement, not a per-branch label: every
  // state but `free` is a real Plus tier on that org — `plus`/`plus-manual`/
  // `family-owner` from its own seatTier, `inherited` from a membership the
  // server caches in inheritedSeatTier. The badge and note below are what
  // distinguish where it came from.
  const isEntitled =
    state.kind !== 'free' &&
    state.kind !== 'dunning' &&
    state.kind !== 'lapsed' &&
    state.kind !== 'inherited-lapsed';
  const title = isEntitled ? t('plan.plus') : t('plan.free');

  // A payment failure or a terminal cancellation. Both already read as Free to
  // every gate, but "Free" alone hides the one thing the owner would act on —
  // and for dunning it would send them to buy a subscription they already have.
  if (state.kind === 'dunning' || state.kind === 'lapsed') {
    const isDunning = state.kind === 'dunning';
    const planName = t(`plan_names.${state.plan}`);
    // Only a date that has actually passed: an immediate cancellation leaves the
    // period end unreached, and "Ended <a future date>" is nonsense.
    const endedOn = formatElapsedPlanPeriodDate(state.endedAt);

    return (
      <PlanCard
        title={title}
        badge={
          <PlanBadge tone="attention">
            {t(
              isDunning
                ? 'interrupted.badge_past_due'
                : 'interrupted.badge_expired'
            )}
          </PlanBadge>
        }
      >
        <Box>
          <Text fontWeight="semibold">
            {t(
              isDunning
                ? 'interrupted.past_due_title'
                : 'interrupted.lapsed_title',
              { plan: planName }
            )}
          </Text>
          <Text color="customgray.mid">
            {isDunning
              ? t('interrupted.past_due_body')
              : endedOn
                ? t('interrupted.lapsed_body', { date: endedOn })
                : t('interrupted.lapsed_body_no_date')}
          </Text>
        </Box>
        <Box>
          {/* Dunning goes to the portal to fix the card — the existing
              subscription is still there and would double-bill if they bought
              another. A terminal one has nothing left to fix, so it needs a
              fresh checkout. */}
          {isDunning ? (
            <ManageSubscriptionButton variant="secondary">
              {t('plan.update_payment')}
            </ManageSubscriptionButton>
          ) : (
            <Button
              variant="secondary"
              onClick={() =>
                redirectToCheckout(
                  'yearly',
                  { launch: 'settings:subscription' },
                  REACTIVATE_PACKAGE[state.plan]
                )
              }
            >
              {t('plan.reactivate')}
            </Button>
          )}
        </Box>
      </PlanCard>
    );
  }

  if (state.kind === 'plus' || state.kind === 'plus-manual') {
    const isManual = state.kind === 'plus-manual';
    // Manual grants don't auto-renew, hence the neutral "Active through"
    // wording. A Stripe subscription set to cancel is `plus-ending` instead, so
    // this branch is always a live renewing plan.
    const dateKey = isManual ? 'plan.plus_active_through' : 'plan.plus_renews';

    return (
      <PlanCard
        badge={<PlanBadge>{t('badge.current_plan')}</PlanBadge>}
        title={title}
      >
        <Text color="customgray.mid">
          {formattedDate && t(dateKey, { date: formattedDate })}
        </Text>
        {isManual ? (
          <ManualPlanSupportNotice />
        ) : (
          <Box>
            <ManageSubscriptionButton variant="secondary">
              {t('plan.manage')}
            </ManageSubscriptionButton>
          </Box>
        )}
      </PlanCard>
    );
  }

  // Canceled but still inside the paid period: keeps granting until then, so the
  // tier is unchanged and only the date's meaning differs. Reachable only
  // because the server now records cancel_at_period_end — Stripe leaves the
  // status `active`, so this used to render as "Renews".
  if (state.kind === 'plus-ending') {
    const endsOn = formatPlanPeriodDate(state.endsAt);

    return (
      <PlanCard
        badge={<PlanBadge>{t('badge.current_plan')}</PlanBadge>}
        title={title}
      >
        <Text color="customgray.mid">
          {endsOn && t('plan.plus_ending', { date: endsOn })}
        </Text>
        <Box>
          <ManageSubscriptionButton variant="secondary">
            {t('plan.resume')}
          </ManageSubscriptionButton>
        </Box>
      </PlanCard>
    );
  }

  if (state.kind === 'inherited' || state.kind === 'family-owner') {
    const isFamily =
      state.kind === 'family-owner' || state.workspace === 'family';

    return (
      <PlanCard
        badge={<PlanBadge>{t('badge.personal_plan')}</PlanBadge>}
        title={title}
      >
        <InheritedAccessNote
          description={t(
            state.kind === 'family-owner'
              ? 'inherited.family_owner_description'
              : isFamily
                ? 'inherited.family_description'
                : 'inherited.business_description'
          )}
          title={t(
            isFamily ? 'inherited.family_title' : 'inherited.business_title',
            { name: state.organization.name }
          )}
        />
        {state.kind === 'family-owner' && (
          <Box>
            <Button
              variant="secondary"
              onClick={() => changeTab('organizations', state.organization.id)}
            >
              {t('inherited.view_account')}
            </Button>
          </Box>
        )}
      </PlanCard>
    );
  }

  // Their Plus came from a workspace whose plan stopped granting. Not theirs to
  // fix, so this explains where it went and leaves the buy path to the pricing
  // grid below — they can get their own.
  if (state.kind === 'inherited-lapsed') {
    return (
      <PlanCard
        title={title}
        badge={
          <PlanBadge tone="attention">
            {t('interrupted.badge_expired')}
          </PlanBadge>
        }
      >
        <InheritedAccessNote
          description={t(
            state.workspace === 'family'
              ? 'interrupted.inherited_body_family'
              : 'interrupted.inherited_body_business'
          )}
          title={t('interrupted.inherited_title', {
            name: state.organization.name,
          })}
        />
        <Box>
          <Button
            variant="secondary"
            onClick={() => changeTab('organizations', state.organization.id)}
          >
            {t(
              state.workspace === 'family'
                ? 'inherited.view_account'
                : 'interrupted.view_workspace'
            )}
          </Button>
        </Box>
      </PlanCard>
    );
  }

  return (
    <PlanCard
      badge={<PlanBadge>{t('badge.current_plan')}</PlanBadge>}
      title={title}
    ></PlanCard>
  );
};
